import {Trans} from "@lingui/react/macro";
import type {RouteObject} from "react-router";
import {CreateDialog, EditDialog} from "../../features/categories/CategoryDialogs";
import {
  CreateCatGroupDialog,
  DeleteCatGroupDialog,
  ManageCatGroupDialog,
} from "../../features/categories/CategoryGroupDialogs";
import CategoryList from "../../features/categories/CategoryList";
import {setCatGroup, useCatGroup} from "../../features/categories/useCategoryGroup";
import {useSortedCategories} from "../../models/category-queries";
import type {CategoryGroupId, GroupEntry, GroupRepo} from "../../models/GroupRepo";
import AppShell from "../../shared/ui/AppShell";
import GroupFooterNav from "../../shared/ui/GroupFooterNav";
import Header from "../../shared/ui/Header";
import {Col, Row} from "../../ui/Box";
import Button from "../../ui/Button";
import {DrawerRoutes, RouteDrawer} from "../../ui/Drawer";
import Icon from "../../ui/Icon";
import {Select} from "../../ui/Input";

const getModalRoutes = (groupRepo: GroupRepo): RouteObject[] => [
  {
    path: "edit/:itemId",
    element: (
      <RouteDrawer backPath=".." title="Edit Category">
        <EditDialog groupRepo={groupRepo} />
      </RouteDrawer>
    ),
  },
  {
    path: "create",
    element: (
      <RouteDrawer backPath=".." title="Create Category">
        <CreateDialog groupRepo={groupRepo} />
      </RouteDrawer>
    ),
  },
  {
    path: "create-category-group",
    element: (
      <RouteDrawer backPath=".." title="Create Category Group">
        <CreateCatGroupDialog groupRepo={groupRepo} />
      </RouteDrawer>
    ),
  },
  {
    path: "manage-category-group",
    element: (
      <RouteDrawer backPath=".." title="Create Category Group">
        <ManageCatGroupDialog groupRepo={groupRepo} />
      </RouteDrawer>
    ),
  },
  {
    path: "delete-category-group",
    element: (
      <RouteDrawer backPath=".." title="delete Category Group">
        <DeleteCatGroupDialog groupRepo={groupRepo} />
      </RouteDrawer>
    ),
  },
];

const GroupSection = ({groupRepo, group}: {groupRepo: GroupRepo; group: GroupEntry}) => {
  const catGroup = useCatGroup(group.id, groupRepo);
  const allCatGroups = Object.values(groupRepo.CategoryGroups.useGetAll());
  return (
    <Row sp="4">
      <Select
        options={allCatGroups.map(({id, name}) => ({label: name || "Default", value: id}))}
        value={catGroup.id}
        onChange={(catGroupId) => setCatGroup(group.id, catGroupId as CategoryGroupId)}
      />
      <Button to="manage-category-group" variant="tertiary">
        <Icon name="cog" />
      </Button>
    </Row>
  );
};

const CategoryOverview = ({groupRepo}: {groupRepo: GroupRepo}) => {
  const group = groupRepo.vals.useValues();
  const catsWithEntries = useSortedCategories(groupRepo, group.id);
  return (
    <AppShell
      header={
        <Header
          titleComponent={<GroupSection groupRepo={groupRepo} group={group} />}
          backButton={{to: "..", label: <Trans id="nav.back">Back</Trans>}}
        />
      }
      footer={<GroupFooterNav current="list" group={group} />}
    >
      <Col px="12" py="12" sp="24">
        {catsWithEntries.length === 0 ? (
          <div>No Categories</div>
        ) : (
          <CategoryList catsWithEntries={catsWithEntries} groupRepo={groupRepo} />
        )}
        <div>
          <Button to="create">Add Category</Button>
        </div>
        <DrawerRoutes routes={getModalRoutes(groupRepo)} />
      </Col>
    </AppShell>
  );
};

export default CategoryOverview;
